import { useCallback, useEffect, useMemo, useState } from 'react';

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

const readStoredPageSize = (storageKey) => {
  if (!storageKey) return DEFAULT_PAGE_SIZE;
  try {
    const stored = Number(window.localStorage.getItem(storageKey));
    return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
  } catch (err) {
    return DEFAULT_PAGE_SIZE;
  }
};

const storePageSize = (storageKey, size) => {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(storageKey, String(size));
  } catch (err) {
    // Storage may be unavailable (private mode); the choice then lasts for the session only.
  }
};

export const usePagination = (items, { storageKey } = {}) => {
  const [pageSize, setPageSizeState] = useState(() => readStoredPageSize(storageKey));
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [items]);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const startIndex = currentPage * pageSize;

  const pageItems = useMemo(
    () => items.slice(startIndex, startIndex + pageSize),
    [items, startIndex, pageSize]
  );

  const setPageSize = useCallback((size) => {
    const next = PAGE_SIZE_OPTIONS.includes(size) ? size : DEFAULT_PAGE_SIZE;
    setPageSizeState(next);
    setPage(0);
    storePageSize(storageKey, next);
  }, [storageKey]);

  const goToPage = useCallback((nextPage) => {
    setPage(Math.max(0, Math.min(nextPage, totalPages - 1)));
  }, [totalPages]);

  return {
    pageItems,
    page: currentPage,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    totalPages,
    total,
    from: total === 0 ? 0 : startIndex + 1,
    to: Math.min(startIndex + pageSize, total),
    setPage: goToPage,
    setPageSize,
  };
};

export default usePagination;
